// V1.0.65: Modal xem lịch sử truy cập tài liệu — chỉ owner/admin.
import { useState } from 'react';
import { Modal, Table, Tag, Spin, Empty, Button } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { History, RefreshCw } from 'lucide-react';
import dayjs from 'dayjs';
import { documentService, type DocumentAccessLog } from '@/services/documentService';

interface Props {
  open: boolean;
  documentId: string;
  documentTitle: string;
  onClose: () => void;
}

const ACTION_LABELS: Record<string, { label: string; color: string }> = {
  VIEW:              { label: 'Xem',              color: 'blue' },
  DOWNLOAD:          { label: 'Tải về',           color: 'cyan' },
  SHARE:             { label: 'Chia sẻ',          color: 'purple' },
  UPDATE_SHARE:      { label: 'Sửa chia sẻ',      color: 'purple' },
  UNSHARE:           { label: 'Thu hồi chia sẻ',  color: 'orange' },
  CHANGE_VISIBILITY: { label: 'Đổi mức bảo mật',  color: 'magenta' },
  CREATE:            { label: 'Tạo mới',          color: 'green' },
  UPDATE:            { label: 'Cập nhật',         color: 'gold' },
  DELETE:            { label: 'Xóa',              color: 'red' },
};

export function DocumentAccessLogModal({ open, documentId, documentTitle, onClose }: Props) {
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['document-access-log', documentId, page],
    queryFn: () => documentService.getAccessLog(documentId, page, pageSize),
    enabled: open && !!documentId,
  });

  const logs = data?.items ?? [];
  const total = data?.total ?? 0;

  const columns = [
    {
      title: 'Thời gian',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (v: string) => dayjs(v).format('DD/MM/YYYY HH:mm:ss'),
    },
    {
      title: 'Người',
      dataIndex: 'username',
      key: 'username',
      width: 180,
      render: (v: string | null, row: DocumentAccessLog) => v ?? (row.userId ? row.userId.slice(0, 8) : '—'),
    },
    {
      title: 'Hành động',
      dataIndex: 'action',
      key: 'action',
      width: 160,
      render: (v: string) => {
        const cfg = ACTION_LABELS[v] ?? { label: v, color: 'default' };
        return <Tag color={cfg.color}>{cfg.label}</Tag>;
      },
    },
    {
      title: 'Chi tiết',
      dataIndex: 'detail',
      key: 'detail',
      render: (v: string | null) => v || '—',
    },
    {
      title: 'IP',
      dataIndex: 'ip',
      key: 'ip',
      width: 140,
      render: (v: string | null) => v || '—',
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="refresh" icon={<RefreshCw size={13} />} onClick={() => refetch()}>
          Làm mới
        </Button>,
        <Button key="close" type="primary" onClick={onClose}>Đóng</Button>,
      ]}
      title={
        <span>
          <History size={16} style={{ verticalAlign: -3, marginRight: 6 }} />
          Lịch sử truy cập — {documentTitle}
        </span>
      }
      width={900}
      destroyOnClose
    >
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
      ) : logs.length === 0 ? (
        <Empty description="Chưa có lịch sử truy cập" />
      ) : (
        <Table<DocumentAccessLog>
          columns={columns as any}
          dataSource={logs}
          rowKey="id"
          size="small"
          pagination={{
            current: page,
            pageSize,
            total,
            onChange: setPage,
            showSizeChanger: false,
            showTotal: (t) => `Tổng ${t} bản ghi`,
          }}
          scroll={{ y: 480 }}
        />
      )}
    </Modal>
  );
}
